import { useWorkforceFilter, WorkforceViewMode } from "@/contexts/WorkforceFilterContext";
import { WORKFORCE_STATUSES, WorkforceStatus } from "@/types/workforce";
import { WorkforceSortField } from "@/hooks/workforce/useWorkforcePaginated";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, X, CalendarIcon, Filter, AlertTriangle, Clock, LayoutGrid, List, Columns, ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export const WorkforceFilterWrapper = () => {
	const { t } = useTranslation();
	const {
		searchTerm,
		setSearchTerm,
		statusFilter,
		setStatusFilter,
		assignedToMe,
		setAssignedToMe,
		dateRangeStart,
		setDateRangeStart,
		dateRangeEnd,
		setDateRangeEnd,
		endingSoon,
		setEndingSoon,
		clearAllFilters,
		viewMode,
		setViewMode,
		sortField,
		setSortField,
		sortOrder,
		setSortOrder,
	} = useWorkforceFilter();

	const hasActiveFilters = searchTerm || statusFilter.length > 0 || assignedToMe || dateRangeStart || dateRangeEnd || endingSoon !== null;

	const handleStatusToggle = (status: WorkforceStatus) => {
		if (statusFilter.includes(status)) {
			setStatusFilter(statusFilter.filter(s => s !== status));
		} else {
			setStatusFilter([...statusFilter, status]);
		}
	};

	const viewModes: { value: WorkforceViewMode; icon: React.ReactNode; label: string }[] = [
		{ value: 'card', icon: <LayoutGrid className="h-4 w-4" />, label: t("workforce.views.card", "Cards") },
		{ value: 'list', icon: <List className="h-4 w-4" />, label: t("workforce.views.list", "List") },
		{ value: 'kanban', icon: <Columns className="h-4 w-4" />, label: t("workforce.views.kanban", "Kanban") },
	];

	const sortOptions: { value: WorkforceSortField; label: string }[] = [
		{ value: 'dateCreated', label: t("workforce.sort.dateCreated", "Date created") },
		{ value: 'startDate', label: t("workforce.sort.startDate", "Start date") },
		{ value: 'candidateName', label: t("workforce.sort.consultantName", "Consultant name") },
		{ value: 'status', label: t("workforce.sort.status", "Status") },
	];

	return (
		<div className="flex flex-col gap-3 mb-6">
			{/* Main filter bar - styled like reference */}
			<div className="flex items-center justify-between px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-sm bg-white dark:bg-gray-900">
				{/* Left side: Sort and filters */}
				<div className="flex items-center gap-4">
					{/* Sort by */}
					{viewMode !== 'kanban' && (
						<div className="flex items-center gap-2">
							<span className="text-sm text-muted-foreground">{t("common.sortBy", "Sort by")}</span>
							<Select value={sortField} onValueChange={(value) => setSortField(value as WorkforceSortField)}>
								<SelectTrigger className="h-8 w-auto min-w-[100px] border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{sortOptions.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<Button
								variant="outline"
								size="sm"
								className="h-8 px-3 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
								onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
							>
								{sortOrder === 'asc' ? t("common.ascending", "Ascending") : t("common.descending", "Descending")}
							</Button>
						</div>
					)}

					{/* Status Filter */}
					<Popover>
						<PopoverTrigger asChild>
							<Button variant="outline" size="sm" className="h-8 gap-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
								<Filter className="h-4 w-4" />
								{t("workforce.filters.status", "Status")}
								{statusFilter.length > 0 && (
									<Badge variant="secondary" className="ml-1 h-5 px-1.5">
										{statusFilter.length}
									</Badge>
								)}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-56" align="start">
							<div className="space-y-2">
								{WORKFORCE_STATUSES.map((status) => (
									<div key={status.value} className="flex items-center space-x-2">
										<Checkbox
											id={`status-${status.value}`}
											checked={statusFilter.includes(status.value)}
											onCheckedChange={() => handleStatusToggle(status.value)}
										/>
										<label
											htmlFor={`status-${status.value}`}
											className="text-sm cursor-pointer"
										>
											{status.label}
										</label>
									</div>
								))}
							</div>
						</PopoverContent>
					</Popover>

					{/* Assigned to Me */}
					<div className="flex items-center space-x-2">
						<Checkbox
							id="assigned-to-me"
							checked={assignedToMe}
							onCheckedChange={(checked) => setAssignedToMe(checked === true)}
							className="rounded-sm"
						/>
						<label htmlFor="assigned-to-me" className="text-sm cursor-pointer whitespace-nowrap">
							{t("workforce.filters.assignedToMe", "Assigned to me")}
						</label>
					</div>

					{/* Ending Soon indicator (when active from stats click) */}
					{endingSoon !== null && (
						<Badge
							variant="secondary"
							className={cn(
								"flex items-center gap-1 px-2.5 py-1 cursor-pointer hover:bg-muted/80",
								endingSoon <= 7 ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400"
							)}
							onClick={() => setEndingSoon(null)}
						>
							{endingSoon <= 7 ? (
								<AlertTriangle className="h-3 w-3" />
							) : (
								<Clock className="h-3 w-3" />
							)}
							{endingSoon <= 7
								? t("workforce.filters.endingThisWeek", "Ending this week")
								: t("workforce.filters.endingSoon", "Ending soon")}
							<X className="h-3 w-3 ml-1" />
						</Badge>
					)}

					{/* Clear filters */}
					{hasActiveFilters && (
						<Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-8 gap-1 text-muted-foreground">
							<X className="h-4 w-4" />
							{t("common.clearFilters", "Clear")}
						</Button>
					)}
				</div>

				{/* Right side: View toggle */}
				<div className="flex items-center gap-3">
					<span className="text-sm text-muted-foreground">{t("common.view", "View")}</span>
					<div className="flex items-center gap-1">
						{viewModes.map((mode) => (
							<Button
								key={mode.value}
								variant="ghost"
								size="sm"
								className={cn(
									"h-8 w-8 p-0",
									viewMode === mode.value && "bg-gray-100 dark:bg-gray-800"
								)}
								onClick={() => setViewMode(mode.value)}
							>
								{mode.icon}
							</Button>
						))}
					</div>
				</div>
			</div>

			{/* Search bar - separate row */}
			<div className="relative max-w-md">
				<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
				<Input
					placeholder={t("workforce.filters.searchPlaceholder", "Search consultants, companies...")}
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className="pl-10 h-9"
				/>
				{searchTerm && (
					<Button
						variant="ghost"
						size="sm"
						className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
						onClick={() => setSearchTerm("")}
					>
						<X className="h-4 w-4" />
					</Button>
				)}
			</div>

			{/* Date filters row (optional, only show when needed) */}
			{(dateRangeStart || dateRangeEnd) && (
				<div className="flex flex-wrap gap-2 items-center">
					<Popover>
						<PopoverTrigger asChild>
							<Button variant="outline" size="sm" className="gap-2 h-8">
								<CalendarIcon className="h-3.5 w-3.5" />
								{dateRangeStart ? format(new Date(dateRangeStart), "MMM d, yyyy") : t("workforce.filters.startFrom", "Start from")}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<Calendar
								mode="single"
								selected={dateRangeStart ? new Date(dateRangeStart) : undefined}
								onSelect={(date) => setDateRangeStart(date ? format(date, "yyyy-MM-dd") : "")}
								initialFocus
							/>
							{dateRangeStart && (
								<div className="p-2 border-t">
									<Button variant="ghost" size="sm" className="w-full" onClick={() => setDateRangeStart("")}>
										Clear
									</Button>
								</div>
							)}
						</PopoverContent>
					</Popover>

					<Popover>
						<PopoverTrigger asChild>
							<Button variant="outline" size="sm" className="gap-2 h-8">
								<CalendarIcon className="h-3.5 w-3.5" />
								{dateRangeEnd ? format(new Date(dateRangeEnd), "MMM d, yyyy") : t("workforce.filters.startUntil", "Start until")}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<Calendar
								mode="single"
								selected={dateRangeEnd ? new Date(dateRangeEnd) : undefined}
								onSelect={(date) => setDateRangeEnd(date ? format(date, "yyyy-MM-dd") : "")}
								initialFocus
							/>
							{dateRangeEnd && (
								<div className="p-2 border-t">
									<Button variant="ghost" size="sm" className="w-full" onClick={() => setDateRangeEnd("")}>
										Clear
									</Button>
								</div>
							)}
						</PopoverContent>
					</Popover>
				</div>
			)}
		</div>
	);
};
